/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs'
import * as Yup from 'yup'

import { commonSchema, createConditionalRequired, validateAgeRange } from './common'
import { ALL_TSHIRT_SIZES, TSHIRT_SIZES } from '../../../constants/models'
import { isCategoryMultisports } from '../../../utils/ApiHelpers'

const validateTandemField = createConditionalRequired((p) => p.isTandem)
const validateRequiredConfirmPhone = createConditionalRequired(
  (p) => !p.isOnsiteRegistration,
)
const validateMatchConfirmPhone = (
  value: any,
  testContext: any,
  matchTo: string,
) => {
  if (!testContext.parent.isOnsiteRegistration) {
    if (value !== undefined && value !== null)
      return value && value.toString().trim().length > 0 && value === matchTo
    return false
  }

  return true
}

const validateJerseyField = createConditionalRequired(
  (p) => p.hasJersey && p.relayEvent?.includes('BIKE'),
)

const ParticipantSchema = Yup.object().shape({
  ...commonSchema,

  // ONSITE REGISTRATION
  isOnsiteRegistration: Yup.boolean(),
  confirmPhoneNumber: Yup.string()
    .max(20, 'Confirm Phone must not exceed 20 characters')
    .test(
      'required-when-not-onsite-registration',
      'Confirm Phone is required',
      validateRequiredConfirmPhone,
    )
    .test(
      'match-to-phoneNumber',
      'Phone numbers must match',
      (value, testContext) => {
        return validateMatchConfirmPhone(
          value,
          testContext,
          testContext.parent.phoneNumber,
        )
      },
    ),
  email: Yup.string().email('Email Address must be a valid email'),

  confirmEmergencyContactPhone: Yup.string()
    .max(20, 'Confirm Emergency Contact Number must not exceed 20 characters')
    .test(
      'required-when-not-onsite-registration',
      'Confirm Emergency Contact Number is required',
      validateRequiredConfirmPhone,
    )
    .test(
      'match-to-emergencyContactPhone',
      'Emergency Contact Phone numbers must match',
      (value, testContext) => {
        return validateMatchConfirmPhone(
          value,
          testContext,
          testContext.parent.emergencyContactPhone,
        )
      },
    ),
  // .required('Confirm Emergency Contact Number is required')
  // .oneOf(
  //   [Yup.ref('emergencyContactPhone'), null],
  //   'Emergency Contact Phone numbers must match',
  // ),

  // TRIATHLON/DUATHLON relay categories
  isTriathlon: Yup.boolean(),
  teamType: Yup.string(),
  relayEvent: Yup.array()
    .of(Yup.string())
    .test(
      'required-with-triathlon',
      'Relay Event is required',
      (value, testContext) => {
        if (isCategoryMultisports(testContext.parent))
          return Array.isArray(value) && value.length > 0
        return true
      },
    ),
  // TANDEM categories
  isTandem: Yup.boolean(),
  tandemAgeRange: Yup.mixed(),
  tandemName: Yup.string()
    .test(
      'required-with-tandem',
      'Tandem participant name is required',
      validateTandemField,
    )
    .test(
      'validate',
      'Tandem participant name must not exceed 50 characters',
      (value) => {
        if (value !== undefined && value !== null)
          return /^[a-z ,.'-]{1,50}$/i.test(value.toString())
        return true
      },
    ),
  tandemBirthdate: Yup.mixed()
    .test(
      'required-with-tandem',
      'Tandem participant birthdate is required',
      validateTandemField,
    )
    .test(
      'validate-format',
      'Tandem participant birthdate must be valid date in format YYYY-MM-DD',
      (value) => {
        if (value !== undefined && value !== null)
          return dayjs(value as string, 'YYYY-MM-DD', true).isValid()
        return true
      },
    )
    .test({
      name: 'validate-range',
      test: (value: any, testContext: any) =>
        validateAgeRange(value, testContext, 'tandemAgeRange'),
    })
    .test(
      'validate-future',
      'Child birthdate must not be a future date',
      (value) => {
        if (value !== undefined && value !== null)
          return dayjs().isAfter(value as string)
        return true
      },
    ),
  tandemShirtSize: Yup.mixed()
    .test(
      'required-with-tandem',
      'Tandem participant t-shirt size is required',
      validateTandemField,
    )
    .oneOf(Object.keys(TSHIRT_SIZES)),
  // Additional ID
  additionalId: Yup.string(),
  // firstParticipants
  firstParticipants: Yup.mixed().nullable(),
  firstParticipantInfo: Yup.string().test({
    name: 'validate-length',
    test: (value: any, testContext: any) => {
      // console.log({ value, testContext })
      if (value !== undefined && value !== null) {
        if ('firstParticipants' in testContext.parent) {
          const length =
            testContext.parent.firstParticipants.INFO_LABEL_LENGTH ?? 25
          if (testContext.parent.firstParticipantInfo.trim().length > length) {
            return testContext.createError({
              message: `Must not exceed ${length} characters`,
              path: testContext.path, // Fieldname
            })
          }
        }
      }
      return true
    },
  }),
  // subCategories
  subCategories: Yup.mixed().nullable(),
  subCategory: Yup.string()
    .test(
      'required-with-subCategory',
      '${label} is required',
      createConditionalRequired(
        (p) =>
          'subCategories' in p &&
          Object.keys(p.subCategories || {}).length > 0 &&
          'required' in p.subCategories &&
          p.subCategories.required,
        (p) => `${p.subCategories.label} is required`,
      ),
    )
    .test({
      name: 'validate-options',
      test: (value: any, testContext: any) => {
        if (
          'subCategories' in testContext.parent &&
          Object.keys(testContext.parent.subCategories || {}).length > 0 &&
          value !== undefined &&
          value !== null
        ) {
          const validOptions =
            testContext.parent.subCategories.options.map((a) => a.name)
          if (!validOptions.includes(value)) {
            return testContext.createError({
              message: `Invalid ${testContext.parent.subCategories.label} selected`,
              path: testContext.path,
            })
          }
        }
        return true
      },
    }),
  jerseySize: Yup.mixed()
    .test('required-with-hasJersey', 'Jersey is required', validateJerseyField)
    .oneOf(Object.keys(ALL_TSHIRT_SIZES)),
})

export default ParticipantSchema
